import React, { Component } from 'react';
import ActionModal, { ActionModalFooter } from 'common-components/modal/ActionModal';
import { Text } from 'common-components/typography';
import { PrimaryButton, SecondaryButton } from 'common-components/buttons';
import _ from 'lodash';
import { IShiftSlot } from 'interfaces/shift-interfaces';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { connect } from 'react-redux';
import { Spinner } from '@blueprintjs/core';
import { ISession } from 'interfaces/session-interfaces';

interface IUnapproveLeaveModalProps {
  isOpen: boolean;
  shiftSlot: IShiftSlot;
  onClose: (params: { targetFlag: string }, refresh?: boolean) => void;
  doRevokeLeaveApproval: (request: {
    serviceId: string;
    serviceDateTimeId: string;
    supportWorkerAttendanceIds: string[];
  }) => Promise<void>;
  session: ISession;
}

interface IUnapproveLeaveModalState {
  step: number;
  canManuallyClose: boolean;
  error: string | null;
}

class UnapproveLeaveModal extends Component<IUnapproveLeaveModalProps, IUnapproveLeaveModalState> {
  override state = {
    step: 1,
    canManuallyClose: true,
    error: null,
  };

  private _reset = () => this.setState({ step: 1, error: null });

  private _onSave = async () => {
    const { doRevokeLeaveApproval, shiftSlot, session } = this.props;

    this.setState({ step: 2, canManuallyClose: false, error: null });

    try {
      const request = {
        serviceId: session.serviceId,
        serviceDateTimeId: session.serviceDateTimeId,
        supportWorkerAttendanceIds: [shiftSlot.supportWorkerAttendanceId],
      };
      await doRevokeLeaveApproval(request);

      this.setState({ step: 3, canManuallyClose: true });
    } catch (error) {
      this.setState({
        step: 4,
        canManuallyClose: true,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    }
  };

  private _onClose = () => {
    const { onClose } = this.props;
    this._reset();
    onClose({ targetFlag: 'isUnapproveLeaveOpen' }, true);
  };

  override render() {
    const { shiftSlot } = this.props;

    if (_.isEmpty(shiftSlot)) {
      return <></>;
    }

    return (
      <ActionModal
        isOpen={this.props.isOpen}
        title='Unapprove leave'
        onClose={this._onClose}
        canCloseOutside={this.state.canManuallyClose}
      >
        {this.state.step === 1 && (
          <div>
            <div className='mb-medium line-height-135'>
              <Text lineHeight={135}>
                You are about to unapprove the leave for the selected shift. <br />
                <br />
                Are you sure you want to continue?
              </Text>
            </div>

            <ActionModalFooter align='right'>
              <SecondaryButton size='large' className='mr-medium' onClick={this._onClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton size='large' onClick={this._onSave}>
                Unapprove leave
              </PrimaryButton>
            </ActionModalFooter>
          </div>
        )}

        {this.state.step === 2 && (
          <div className='line-height-135 anim-slide-left'>
            <div className='flex-column align-center mv-large justify-center'>
              <div className='mb-medium'>
                <Spinner size={80} />
              </div>
              <div className='text-align-center'>
                <Text color='secondary' weight='bold'>
                  Unapproving leave...
                </Text>
                <br />
                <Text color='secondary'>This won't take long.</Text>
              </div>
            </div>
          </div>
        )}

        {this.state.step === 3 && (
          <div className='anim-fade-in-fast'>
            <div className='mb-medium line-height-135'>
              <Text lineHeight={135}>You have successfully unapproved the leave for this shift.</Text>
            </div>

            <ActionModalFooter align='right'>
              <PrimaryButton size='large' onClick={this._onClose}>
                Close
              </PrimaryButton>
            </ActionModalFooter>
          </div>
        )}

        {this.state.step === 4 && (
          <div className='anim-fade-in-fast'>
            <div className='mb-medium line-height-135'>
              <Text lineHeight={135} color='danger'>
                There was an error unapproving the leave for this shift.
              </Text>
              {this.state.error && (
                <div className='mt-small'>
                  <Text color='secondary' size='small'>
                    {this.state.error}
                  </Text>
                </div>
              )}
            </div>

            <ActionModalFooter align='right'>
              <SecondaryButton size='large' className='mr-medium' onClick={this._onClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton size='large' onClick={this._onSave}>
                Try again
              </PrimaryButton>
            </ActionModalFooter>
          </div>
        )}
      </ActionModal>
    );
  }
}

const mapState = (state: IRootState) => ({});

const mapDispatch = (dispatch: IRootDispatch) => ({
  doRevokeLeaveApproval: dispatch.groupServiceStore.doRevokeLeaveApproval,
});

export default connect(mapState, mapDispatch as any)(UnapproveLeaveModal);
